import React from "react";
import AppHeader from "../components/AppHeader";
import { Link } from "react-router-dom";

const sectionStyle = {
  background: "var(--bg-muted)",
  borderRadius: "10px",
  padding: "1rem 1.25rem",
  marginBottom: "1.25rem",
  boxShadow: "inset 0 0 4px rgba(0,0,0,0.08)",
};

const h3Style = {
  color: "var(--accent-blue-dark)",
  borderBottom: "1px solid var(--border)",
  paddingBottom: "0.25rem",
  marginBottom: "0.75rem",
  marginTop: 0,
};

export default function About() {
  return (
    <>
      <AppHeader />

      <div
        style={{
          maxWidth: "800px",
          margin: "2rem auto",
          padding: "0 1rem 2rem",
          lineHeight: "1.7",
        }}
      >
        <h2 className="page-header" style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          📘 About CardStoard
        </h2>

        {/* Purpose */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>🎯 Purpose</h3>
          <p style={{ margin: 0 }}>
            <strong>CardStoard</strong> is a personal web application built for collectors who want a fast,
            simple, and powerful way to manage and value their sports card collections. It was designed
            with speed, accuracy, and simplicity in mind — built by a collector, for collectors. At its
            core is a configurable valuation engine that estimates market value based on book values,
            card grade, and rookie status, giving you a real-time picture of what your collection is worth.
          </p>
        </div>

        {/* Getting Around */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>🧭 Getting Around</h3>
          <p style={{ marginTop: 0 }}>The navigation bar at the top gives you quick access to everything:</p>
          <ul style={{ marginBottom: "0.5rem" }}>
            <li><strong>📋 My Cards</strong> — browse, filter, sort, edit, and manage your full collection</li>
            <li><strong>📈 Analytics</strong> — charts and trends across your collection</li>
            <li><strong>⚙️</strong> (gear icon) — Admin: settings, valuation factors, player dictionary, card import, and data management</li>
            <li><strong>Your username</strong> — account settings, password changes, and multi-factor authentication</li>
            <li><strong>📸 Batch Capture</strong> — bulk photo session to photograph your collection card-by-card</li>
            <li><strong>💬</strong> (chat icon, if enabled) — AI-powered Collection Assistant</li>
            <li><strong>📘 About</strong> — this page</li>
          </ul>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-muted)" }}>
            For a step-by-step walkthrough of every feature, see the{" "}
            <Link to="/user-guide" style={{ color: "var(--link)", fontWeight: 600 }}>User Guide</Link>.
          </p>
        </div>

        {/* Core Features */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>🧩 Core Features</h3>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Collection Management</p>
          <ul style={{ marginTop: 0 }}>
            <li>Add, edit, and delete cards with front &amp; back image uploads</li>
            <li><strong>Batch Capture</strong> — step-through photo session for bulk card photography; camera or file input; crop/rotate editor; duplicate detection with photo swap; session auto-saves and resumes</li>
            <li>Inline editing directly in the card list — no page navigation required</li>
            <li>Bulk <Link to="/import-help" style={{ color: "var(--link)", fontWeight: 600 }}>import</Link> from CSV</li>
            <li>Filter by player, brand, year, grade, and rookie status</li>
            <li>Advanced multi-column sort with saved default sort order</li>
            <li>Book values auto-propagate to all matching cards on save</li>
            <li>Row highlights: Mint grade (lavender), Rookie (gold), Rookie + Mint (rose)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Valuation Engine</p>
          <ul style={{ marginTop: 0 }}>
            <li>Book value inputs: Hi, Hi-Mid, Mid, Lo-Mid, Lo</li>
            <li>Configurable grade factors (MT, EX, VG, GD, FR, PR) and Rookie multiplier</li>
            <li>Apply global revaluation across your entire collection from Admin</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Label Printing &amp; QR Codes</p>
          <ul style={{ marginTop: 0 }}>
            <li>Print a single card label with a live preview — Avery 6427 format (1.75" × 0.75")</li>
            <li>Batch printing: select individual cards or print your entire collection at once</li>
            <li>Each label includes a QR code — scan to open a public shareable view (cards and sets/binders)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Player Dictionary</p>
          <ul style={{ marginTop: 0 }}>
            <li>Searchable database of players, brands, years, and card numbers — 1,875+ entries covering Topps 1952–1980, Bowman 1948–1955, Fleer 1960–1963, Donruss 1981–1990, Upper Deck 1989</li>
            <li>Smart Fill: auto-populates card number and rookie flag when adding or editing cards</li>
            <li>Player name autocomplete with Tab / Enter key completion</li>
            <li>Import dictionary entries via CSV; add and edit individual entries</li>
            <li>In-collection highlights: dictionary rows matching your cards appear in green</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Analytics</p>
          <ul style={{ marginTop: 0 }}>
            <li>Collection breakdown by brand, year, and player — click any row to filter your card list</li>
            <li>Total value tracking with historical trend charts (Inventory, Valuation, Combined)</li>
            <li>Inventory growth over time; year filter for focused analysis</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Data Management</p>
          <ul style={{ marginTop: 0 }}>
            <li>Extract card data as CSV, TSV, or JSON for use in external tools</li>
            <li>Full backup (cards + settings) and restore — available in Admin</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Collection Assistant</p>
          <ul style={{ marginTop: 0 }}>
            <li>AI-powered chat with context about your collection (powered by Claude)</li>
            <li>Accurate counts via pre-computed player, grade, and brand summaries</li>
            <li>Enable or disable from Admin settings</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Account &amp; Security</p>
          <ul style={{ marginTop: 0, marginBottom: 0 }}>
            <li>Email verification and secure JWT session handling with auto token refresh</li>
            <li>Optional TOTP multi-factor authentication <em>(coming soon)</em></li>
            <li>Update username, email, and password from your Account page</li>
            <li>Dark mode — toggle in Admin, persists across sessions</li>
          </ul>
        </div>

        {/* Version */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>📦 Current Version</h3>
          <p style={{ margin: 0 }}><strong>CardStoard v1.49</strong></p>
        </div>

        {/* Recent Updates */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>🆕 Recent Updates</h3>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.49 (Sep 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>PDF collection report — generate a landscape A4 PDF from the Analytics page; includes cards, auto balls, wax boxes, wax packs, and sets/binders; configurable sort, scope, and valued-only filter</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.48 (Aug 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Autonomous kiosk/expo demo — 6 loops cycling through Cards, Gallery, Analytics, Inventory, AI, and Admin</li>
            <li>Live Smart Fill typing demo — watch fields auto-populate character by character</li>
            <li>Gallery loop: each card shown front and back; zoom in/out; backward navigation</li>
            <li>Pause overlay with jump-to-loop buttons (A–F); <code>?loop=X</code> URL param to start at a specific loop</li>
            <li>Sort demo pre-loads a 3-level config so modal opens fully configured</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.47 (Jul 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>18-step guided tour — navigates through Cards, Gallery, and Analytics with spotlight zoom</li>
            <li>Sign Up Free button in demo banner; Exit Tour button; per-step bubble positioning</li>
            <li>Register page: centered layout with CardStoard logo</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.46 (Jul 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Live demo mode — Try a Demo button on login, read-only showcase with real collection data</li>
            <li>Demo welcome panel, amber banner, focused nav (Cards / Gallery / Analytics)</li>
            <li>Tooltip system — amber styled tooltips on card list headers, analytics rows, gallery tiles</li>
            <li>Filter debounce — type full names without losing focus; Hide Dupes updates count and value</li>
            <li>Backup/restore extended to all inventory types (balls, wax, packs, binders)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.45 (Jul 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Server-side pagination — sort and filter apply to full collection, not just loaded page</li>
            <li>Cards Per Page setting in Admin → General Settings (100/250/500/1000/All)</li>
            <li>Toolbar shows X of Y cards with ＋ control to load next page; Load More at grid bottom</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.44 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Pack Pull toggle on Card Detail — purple chip beside PP/PD, click to mark a card as pack-pulled</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.43 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Purchase date tracking — captured on add, editable as click-to-edit chip in card list and Card Detail</li>
            <li>Hide/Show Dupes toggle — deduplicates by player/brand/year/card#, keeps highest-value card; count updates to reflect visible cards</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.42 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Serial number field for graded cards (appears below grade selector)</li>
            <li>Autograph authenticator dropdown (JSA/PSA/SGC/BKT) in card variant panel</li>
            <li>Price Paid tracking — captured on add, editable in list grid and Card Detail</li>
            <li>New users see empty card grid with add/import prompts instead of blank page</li>
            <li>Nginx maintenance page (502/503/504) with CardStoard logo</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.41 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Analytics expanded to all inventory types — Auto Balls, Wax Boxes, Wax Packs, Sets/Binders each get collapsible sections with sortable breakdown tables and bar charts; aggregate collection overview at top</li>
            <li>Customizable wax box and pack types — add Vending and custom types from Admin Settings</li>
            <li>Route rename: Sets/Binders → /set-binders, Builds (Set Builder) → /builds; nav guard bug fixes</li>
            <li>Sets/Binders inline add — no more separate form page</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.40 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Subscription tier system (Base / RC / SP / Elite) — card limits, feature gates, and nav access enforced per tier</li>
            <li>Admin → Users tab: assign tiers per user; applying a tier atomically resets feature flags and nav items to the tier preset</li>
            <li>Tier shown on Account page under Subscription Tier</li>
            <li>Label printing, Set Builder, Image AI, and Chatbot now Elite-only; Graded Cards at SP+; Smart Fill, Gallery, Analytics at RC+</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.39 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>"Forgot password?" link on login — enter your email and receive a one-time reset link valid for 1 hour</li>
            <li>After deleting or adding a card, the list scrolls to the relevant row automatically</li>
            <li>Book values entered when adding a new card no longer show a red freshness border immediately</li>
            <li>Analytics breakdown tables no longer duplicate unknown-brand rows when sorting</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.38 (Jun 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Graded card support (PSA/SGC/BGS) — enable via Admin → Settings → Features; graded cards use a 1–10 numeric grade and manually-entered market value instead of the book-value formula</li>
            <li>Grade column shows teal "PSA 9" / "SGC 8" badge for graded cards; book values show "—"; value badge is light blue</li>
            <li>Grade ≥ 9 cards participate in row color coding (same as raw Mint)</li>
            <li>Filter by PSA / SGC / BGS / Raw via the Grade column filter (🔍)</li>
            <li>Pin row now shows a yellow flash after saving; grey pinned-row background removed</li>
            <li>Cards with no image now show a muted 📷 icon instead of "No Image" text</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.37 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Valuation factors are now managed by CardStoard — updated factors are published to your account automatically</li>
            <li>⚡ Banner on home screen and Admin → Valuation tab when new factors are available; one-tap "Apply to My Collection"</li>
            <li>Last valuation timestamp shown in Admin → Valuation and on the home screen</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.36 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>11 built-in variant types (Ref, SP, Auto, AP, PP, NNO, etc.) — toggle which appear in your editing panel via Admin settings</li>
            <li>Add your own custom variant types (4-char abbr + description, up to 20)</li>
            <li>Chips in the card list and Gallery show full variant names</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.35 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Seed from My Cards now shows a detailed breakdown: cards processed, unique entries written, duplicates, and value dictionary total</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.34 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Gallery multi-level sort — all 9 criteria matching the card list; sort default saved per-browser</li>
            <li>Card attribute chips (parallel, auto, numbered, etc.) shown in Gallery</li>
            <li>Layout polish — sort button, zoom controls, counter</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.33 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>JSON backup now includes image paths, notes, and card attributes — restoring a backup preserves photos</li>
            <li>Scheduled server backup now archives card images alongside the database</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.32 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Gallery page — browse card photos one at a time with 3D flip (front↔back) and cross-fade navigation</li>
            <li>Search, sort, and resize cards; keyboard shortcuts for navigation and flip</li>
            <li>Enable Gallery from Admin → Settings → Nav Bar</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.31 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Card Detail image area auto-focuses on load — horizontal scroll works immediately without clicking first</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.30 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Card Detail images centered on page; scroll left and right when larger than the viewport</li>
            <li>Canceling an inline card edit no longer jumps to the top of the list</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.29 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Delete front or back images from the inline card edit row — red ✕ next to each uploaded photo</li>
            <li>Delete Photo option in the photo editor when re-editing an existing image (with confirm step)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.28 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Photo editor remembers your settings — click Save Default to persist brightness, contrast, saturation, and crop box position</li>
            <li>Portrait and landscape crop defaults saved independently</li>
            <li>Reset clears all saved defaults back to factory</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.27 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Inline image upload in card list now opens the full photo editor (crop, rotate, adjust) before saving</li>
            <li>Existing card photos can be re-edited via a new Edit button in the image viewer</li>
            <li>Crop box hidden by default — toggle on demand with ✂ Crop</li>
            <li>Photo editor modal respects dark/light mode</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.26 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Row color scheme corrected — gold highlight now for Rookie+MT (grade 3.0), blue for other-grade rookies</li>
            <li>$0 value cards show a grey/muted chip instead of a colored tier badge</li>
            <li>"No Image" text darkened on gold rows for legibility</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.25 (May 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Book value propagation now scoped to matching card variants — base card prices no longer overwrite refractor or autograph duplicate pricing</li>
            <li>Autograph attribute uses Auto Factor for valuation; new Rookie MT Factor separates Rookie+Mint grade valuation — both independently configurable in Admin</li>
            <li>Return-from-detail yellow highlight now clears after 10 seconds</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.24 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Book freshness indicator moved to the left border of the Book cell (was on the far-left row edge)</li>
            <li>Delete confirmation is now an inline modal — row above the target card pins immediately on click so you can see where you'll land</li>
            <li>"Back to List" from Card Detail scrolls back to the card you were viewing with a yellow highlight</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.23 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Card value badge colors now match book value badge colors — darker green = lower value, brighter green = higher value; amber badge preserved for values above book-high</li>
            <li>New Admin action: "Clear Book Value Timers" — nullifies book freshness timestamps on all cards, flagging your entire collection for a price review pass</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.22 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Book freshness indicator now correctly flags cards with no book values, even when the timestamp was set by CSV import</li>
            <li>Fixed scroll-to-top when saving inline edits on cards with duplicates — table now stays in place after book value propagation</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.21 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>ImageEditor: portrait/landscape toggle — flip crop aspect ratio for horizontal cards</li>
            <li>ImageEditor: brightness, contrast, and saturation sliders with real-time preview; baked into saved photo</li>
            <li>ImageEditor: fixed 1500×2100 (portrait) / 2100×1500 (landscape) export resolution regardless of source size</li>
            <li>Camera upgraded to 4K ideal resolution in Batch Capture and Scan</li>
            <li>Scan page: photo editing/cropping now applied to both camera capture and file upload before AI identification</li>
            <li>Zoom modal: full image reachable by scrolling — no more truncation at edges; Front/Back/Close toolbar fixed at bottom</li>
            <li>Batch Capture setup: "First 5 in queue" preview shows card order before starting, updates live with filter/sort changes</li>
            <li>My Cards: hover over any thumbnail to see a floating 220px preview without clicking</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.20 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Batch Capture — step through your collection card-by-card to photograph front and back; camera or file input; crop/rotate editor; duplicate detection with photo swap; session saves and resumes automatically</li>
            <li>Inline image upload — add or replace front/back photos directly from the card edit row in My Cards</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.19 (April 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>AI Image Recognition — photograph or upload a card; AI identifies the player, brand, year, and card number and pre-fills the Add Card form</li>
            <li>Camera capture directly in the browser (mobile and desktop)</li>
            <li>Player Dictionary expanded to 28,800+ entries — full Topps 1952–1990 checklist (every player, every year)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.18 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Internal code quality — duplicate component logic extracted into shared modules (no user-facing changes)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.17 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Code quality improvements — 0 bugs, 0 vulnerabilities (SonarCloud)</li>
            <li>Accessibility improvements across card list, set cards, modals, and image viewer</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.16 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Collection Assistant (Cy) now knows about all inventory types — ask about your Balls, Wax Boxes, Wax Packs, and Sets/Binders</li>
            <li>Login with either your username or email address</li>
            <li>Code quality improvements (SonarCloud remediations)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.15 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Wax Boxes: new inventory type for sealed wax boxes (year, brand, Cello/Rack/Std type badge, quantity, value freshness, label print + QR)</li>
            <li>Wax Packs: new inventory type for individual packs (year, brand, Cello/Rack/Wax/Blister type badge, quantity, value freshness, label print + QR)</li>
            <li>📦 Wax and 🎴 Packs nav buttons activated</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.14 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Auto Balls: new inventory type for autographed baseballs (signer, brand, commissioner, COA auth badge, inscription, value, label print + QR)</li>
            <li>⚾ Balls nav button activated — full list page with inline editing, freshness tracking, and label printing</li>
            <li>Pin / bookmark now persisted to your account profile (follows you across devices)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.13 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Value Dictionary: book values stored on dictionary entries (brand + year + card number)</li>
            <li>Smart Fill now auto-populates all 5 book value tiers when adding a card</li>
            <li>Admin: seed book values from existing cards; bulk import via CSV</li>
            <li>Dictionary list: Values column shows which entries have book values on file</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.12 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Inline code documentation added to 21 key backend and frontend files</li>
            <li>File-level headers, function docstrings, and component descriptions throughout</li>
            <li>No feature or logic changes — documentation only</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.11 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Dictionary expanded to 97 players / 1,875+ entries — added Bowman 1948–1955, Fleer 1960–1963, Donruss/Fleer/Upper Deck 1981–1990</li>
            <li>Smart Fill now covers major brands for the modern era (Griffey Jr., Bonds, Clemens, Mattingly, Puckett, Ripken, and more)</li>
            <li>Full vintage set checklists in Sets/Builds: Bowman 1948–1955 (8 sets) and Fleer 1959–1963 (4 sets)</li>
            <li>All Topps 1952–1990 set checklists corrected to 100% complete (39 sets, 24,674 entries)</li>
            <li>seed_dictionary.py changed to additive — new player entries auto-seed on deploy without wiping existing data</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.10 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Sets/Binders renamed from Boxes/Binders; Builds renamed from Sets in nav</li>
            <li>CS-ST-XXXXXX identifier + Avery 6427 label printing with QR code for Sets/Binders</li>
            <li>Set Detail page with stats, editable notes, label printing, and prev/next navigation</li>
            <li>Notes appear as a 4th line on set/binder labels; QR scan opens public set view</li>
            <li>Inline action controls for Sets/Binders: copy, detail, print</li>
            <li>Quantity and total columns for Sets/Binders</li>
            <li>Nav bar customization — show/hide nav buttons per user (Admin)</li>
            <li>Last login date/time displayed on Account page</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.9 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Sets — browse Topps 1952–1990 checklists (39 sets, 17,504 cards) and track your build</li>
            <li>Boxes &amp; Binders — track complete sets as inventory items (factory, collated, binder)</li>
            <li>Per-user set visibility — Admin chip picker to choose which sets appear on your Sets page</li>
            <li>Sort modal improvements — wider modal, save-as-default for Boxes sort</li>
            <li>Analytics combined chart Y-axis label fix; nav bar cleanup</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.8 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Pin / bookmark any row — persists across sessions; auto-pins after every save</li>
            <li>Book freshness ↻ button — reset timer per row or in bulk from Admin</li>
            <li>CD player nav controls replace pagination in the Cards list</li>
            <li>Clone / edit scroll preservation — table stays in place after saves</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.6 (March 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Book value propagation — changes update all matching cards automatically</li>
            <li>Advanced multi-column sort modal with saved default sort order</li>
            <li>Batch label printing — print selected cards or full collection</li>
            <li>Inline edit UX — saved cards land in correct sorted position</li>
            <li>Dark mode now applies before page render (no more flash of light mode)</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.5 (February 2026)</p>
          <ul style={{ marginTop: 0 }}>
            <li>Player dictionary expanded to 867+ entries (Topps 1952–1980)</li>
            <li>Player name autocomplete with Tab / Enter key</li>
            <li>In-collection green highlights in dictionary view</li>
          </ul>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>v1.4 (February 2026)</p>
          <ul style={{ marginTop: 0, marginBottom: 0 }}>
            <li>Dark mode — full CSS theme system, per-user toggle in Admin</li>
            <li>Account management UX improvements</li>
            <li>EC2 deploy automation with pre-deploy database backup</li>
          </ul>
        </div>

        {/* Disclaimer */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>📜 Disclaimer</h3>
          <p style={{ margin: 0 }}>
            CardStoard is a personal project and is not affiliated with or endorsed by any sports
            or grading organizations. All card images and valuations are user-generated or based
            on publicly available market data.
          </p>
        </div>

        {/* Contact */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>📩 Contact</h3>
          <p style={{ margin: 0 }}>
            Feedback or issues? Email: <strong>cardstoard@gmail.com</strong>
          </p>
        </div>

        <p style={{ textAlign: "center", fontSize: "0.9rem", color: "var(--text-faint)", marginTop: "1rem" }}>
          © CardStoard {new Date().getFullYear()} — All rights reserved.
        </p>
      </div>
    </>
  );
}
